import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/constants";

function Markdown({ text }) {
  const blocks = (text || "").split("\n");
  return (
    <div className="space-y-1.5" data-testid="ai-markdown">
      {blocks.map((line, i) => {
        const bold = (s) =>
          s.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
            part.startsWith("**") && part.endsWith("**") ? (
              <strong key={j} className="font-bold text-[#2A1810]">{part.slice(2, -2)}</strong>
            ) : (
              <span key={j}>{part}</span>
            )
          );
        if (/^###\s/.test(line))
          return <h4 key={i} className="font-display text-sm font-bold text-[#2A1810] pt-2">{line.replace(/^###\s/, "")}</h4>;
        if (/^##\s/.test(line))
          return <h3 key={i} className="font-display text-base font-bold text-[#1C100B] pt-3">{line.replace(/^##\s/, "")}</h3>;
        if (/^\s*[-*]\s/.test(line))
          return (
            <div key={i} className="flex gap-2 text-sm text-[#5C4A3E] leading-relaxed">
              <span className="text-[#C5A059] mt-0.5">•</span>
              <span>{bold(line.replace(/^\s*[-*]\s/, ""))}</span>
            </div>
          );
        if (!line.trim()) return null;
        return <p key={i} className="text-sm text-[#5C4A3E] leading-relaxed">{bold(line)}</p>;
      })}
    </div>
  );
}

export default function AIPanel({ title, description, endpoint, cacheKey, initial, onGenerated, testid }) {
  const { canWrite } = useAuth();
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState(initial || null);

  const { data: config } = useQuery({
    queryKey: ["ai-config"],
    queryFn: async () => (await api.get("/ai/config")).data,
  });

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.post(endpoint);
      setOutput(res.data);
      toast.success("AI analysis ready");
      onGenerated?.(res.data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const disabled = config && config.enabled === false;

  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-[#E5C992] bg-[#F9F3E6]/60 p-6"
      data-cache-key={cacheKey}
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-[#C5A059]/20 flex items-center justify-center shrink-0">
          <Sparkles size={18} className="text-[#B08B46]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="font-semibold text-[#2A1810] text-sm">{title}</div>
              <p className="text-xs text-[#8C7A6B] mt-1 leading-relaxed max-w-xl">{description}</p>
            </div>
            {canWrite && (
              <button
                onClick={generate}
                disabled={loading || disabled}
                data-testid={`${testid}-generate-button`}
                className="shrink-0 bg-[#2A1810] hover:bg-[#3D2314] text-[#FDFBF7] px-4 py-2.5 rounded-lg font-semibold text-sm transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50"
              >
                {loading ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {loading ? "Analysing…" : output ? "Regenerate" : "Generate"}
              </button>
            )}
          </div>

          {disabled && (
            <p className="text-xs text-[#B91C1C] mt-3" data-testid={`${testid}-disabled`}>
              AI features are currently switched off in Settings.
            </p>
          )}

          {output ? (
            <div className="mt-5 bg-white border border-[#E8DFD1] rounded-lg p-5" data-testid={`${testid}-output`}>
              <Markdown text={output.content} />
              <div className="mt-4 pt-3 border-t border-[#F4EFEA] text-[10px] text-[#8C7A6B] leading-relaxed">
                <span className="font-semibold text-[#8A6D2F] uppercase tracking-wider">AI suggestion — requires R&D validation.</span>{" "}
                Generated by {output.generated_by_name || "—"} on {fmtDateTime(output.created_at)} · {output.model}
              </div>
            </div>
          ) : (
            <p className="text-xs text-[#8C7A6B] mt-4 italic" data-testid={`${testid}-empty`}>
              No analysis generated yet.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
